// ===== Production Validation & Sanitization Utilities =====

import { SECURITY_RULES, VALIDATION_PATTERNS } from '../constants/ChatConstants';

/**
 * Sanitizes user input to prevent XSS attacks
 * @param {string} input - Raw user input
 * @returns {string} - Sanitized input
 */
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') {
    return String(input || '');
  }

  let sanitized = input;

  // Remove script tags and javascript: URLs
  SECURITY_RULES.XSS_PATTERNS.forEach(pattern => {
    sanitized = sanitized.replace(pattern, '');
  });

  // Encode HTML entities
  sanitized = sanitized
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');

  // Remove null bytes and control characters
  sanitized = sanitized.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');

  return sanitized.trim();
};

/**
 * Validates message content
 * @param {string} message - Message to validate
 * @returns {boolean} - True if valid
 */
export const validateMessage = (message) => {
  if (!message || typeof message !== 'string') {
    return false;
  }

  // Check length
  if (message.length === 0 || message.length > SECURITY_RULES.MAX_MESSAGE_LENGTH) {
    return false;
  }

  // Check for malicious patterns
  const hasMaliciousContent = SECURITY_RULES.XSS_PATTERNS.some(pattern => 
    pattern.test(message)
  );

  if (hasMaliciousContent) {
    return false;
  }

  // Check for excessive whitespace
  if (message.trim().length === 0) {
    return false;
  }

  return true;
};

/**
 * Validates email format
 * @param {string} email - Email to validate
 * @returns {boolean} - True if valid
 */
export const validateEmail = (email) => {
  return typeof email === 'string' && 
         email.length > 0 && 
         email.length <= 254 && 
         VALIDATION_PATTERNS.EMAIL.test(email);
};

/**
 * Validates phone number format
 * @param {string} phone - Phone number to validate
 * @returns {boolean} - True if valid
 */
export const validatePhone = (phone) => {
  return typeof phone === 'string' && 
         phone.length >= 10 && 
         phone.length <= 20 && 
         VALIDATION_PATTERNS.PHONE.test(phone);
};

/**
 * Validates URL format
 * @param {string} url - URL to validate
 * @returns {boolean} - True if valid
 */
export const validateUrl = (url) => {
  if (typeof url !== 'string' || url.length === 0) {
    return false;
  }

  try {
    const urlObj = new URL(url);
    return ['http:', 'https:'].includes(urlObj.protocol);
  } catch {
    return false;
  }
};

/**
 * Validates socket ID format
 * @param {string} socketId - Socket ID to validate
 * @returns {boolean} - True if valid
 */
export const validateSocketId = (socketId) => {
  return typeof socketId === 'string' && 
         socketId.length > 0 && 
         socketId.length <= 50 && 
         VALIDATION_PATTERNS.SOCKET_ID.test(socketId);
};

/**
 * Validates file upload
 * @param {File} file - File to validate
 * @returns {Object} - Validation result
 */
export const validateFile = (file) => {
  const result = {
    valid: false,
    error: null,
    sanitizedName: ''
  };

  if (!file || !(file instanceof File)) {
    result.error = 'Invalid file object';
    return result;
  }

  // Check file size
  if (file.size > SECURITY_RULES.MAX_FILE_SIZE) {
    result.error = `File too large. Maximum size: ${SECURITY_RULES.MAX_FILE_SIZE / (1024 * 1024)}MB`;
    return result;
  }

  // Check file type
  const isImageAllowed = SECURITY_RULES.ALLOWED_IMAGE_TYPES.includes(file.type);
  const isFileAllowed = SECURITY_RULES.ALLOWED_FILE_TYPES.includes(file.type);
  
  if (!isImageAllowed && !isFileAllowed) {
    result.error = 'File type not allowed';
    return result;
  }

  // Check file extension
  const extension = '.' + file.name.split('.').pop().toLowerCase();
  if (SECURITY_RULES.BLOCKED_EXTENSIONS.includes(extension)) {
    result.error = 'File extension not allowed';
    return result;
  }

  // Sanitize filename
  result.sanitizedName = sanitizeFileName(file.name);
  result.valid = true;

  return result;
};

/**
 * Sanitizes filename
 * @param {string} filename - Original filename
 * @returns {string} - Sanitized filename
 */
export const sanitizeFileName = (filename) => {
  if (typeof filename !== 'string') {
    return 'untitled';
  }

  // Remove path traversal attempts
  let sanitized = filename.replace(/\.\./g, '');
  
  // Remove special characters except dots, hyphens, underscores
  sanitized = sanitized.replace(/[^\w\s.-]/g, '');
  
  // Replace multiple spaces with single space
  sanitized = sanitized.replace(/\s+/g, ' ');
  
  // Trim and limit length
  sanitized = sanitized.trim().substring(0, 100);
  
  // Ensure we have something
  if (sanitized.length === 0) {
    sanitized = 'untitled';
  }

  return sanitized;
};

/**
 * Rate limiting validator
 */
export class RateLimiter {
  constructor(maxRequests = 10, windowMs = 60000) {
    this.maxRequests = maxRequests;
    this.windowMs = windowMs;
    this.requests = new Map();
  }

  /**
   * Check if request is allowed
   * @param {string} identifier - User/IP identifier
   * @returns {boolean} - True if allowed
   */
  isAllowed(identifier) {
    const now = Date.now();
    const userRequests = this.requests.get(identifier) || [];
    
    // Remove old requests outside the window
    const validRequests = userRequests.filter(
      timestamp => now - timestamp < this.windowMs
    );

    if (validRequests.length >= this.maxRequests) {
      return false;
    }

    // Add current request
    validRequests.push(now);
    this.requests.set(identifier, validRequests);

    return true;
  }

  /**
   * Get remaining requests for identifier
   * @param {string} identifier - User/IP identifier
   * @returns {number} - Remaining requests
   */
  getRemainingRequests(identifier) {
    const now = Date.now();
    const userRequests = this.requests.get(identifier) || [];
    const validRequests = userRequests.filter(
      timestamp => now - timestamp < this.windowMs
    );

    return Math.max(0, this.maxRequests - validRequests.length);
  }

  /**
   * Clear all rate limit data
   */
  clear() {
    this.requests.clear();
  }
}

/**
 * Content Security Policy helpers
 */
export const CSP = {
  /**
   * Check if URL is safe for embedding
   */
  isSafeUrl: (url) => {
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.toLowerCase();
      
      // Block localhost and private IPs in production
      if (process.env.NODE_ENV === 'production') {
        if (hostname === 'localhost' || 
            hostname.startsWith('127.') ||
            hostname.startsWith('192.168.') ||
            hostname.startsWith('10.') ||
            hostname.match(/^172\.(1[6-9]|2[0-9]|3[0-1])\./)) {
          return false;
        }
      }

      return ['http:', 'https:'].includes(urlObj.protocol);
    } catch {
      return false;
    }
  },

  /**
   * Generate nonce for inline scripts
   */
  generateNonce: () => {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode(...array));
  }
};

/**
 * Input validation schemas
 */
export const ValidationSchemas = {
  user: {
    socketId: validateSocketId,
    email: validateEmail,
    phone: validatePhone
  },
  
  message: {
    text: validateMessage,
    type: (type) => ['text', 'image', 'file', 'audio', 'video'].includes(type)
  },
  
  call: {
    type: (type) => ['audio', 'video'].includes(type),
    sessionId: (id) => typeof id === 'string' && id.length > 0
  },

  file: validateFile
};

export default {
  sanitizeInput,
  validateMessage,
  validateEmail,
  validatePhone,
  validateUrl,
  validateSocketId,
  validateFile,
  sanitizeFileName,
  RateLimiter,
  CSP,
  ValidationSchemas
};
